import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Frostbound';
const BASE_URL = 'https://www.frostbound.org';
const DEFAULT_IMAGE = `${BASE_URL}/favicon.png`;

/**
 * @param {{
 *   title?: string,
 *   description?: string,
 *   canonical?: string,
 *   ogImage?: string,
 *   noIndex?: boolean,
 *   noFollow?: boolean,
 *   jsonLd?: object|object[]
 * }} props
 */
export default function SEOMeta({
  title,
  description,
  canonical,
  ogImage,
  noIndex = false,
  noFollow = false,
  jsonLd,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const robotsContent = [noIndex ? 'noindex' : 'index', noFollow ? 'nofollow' : 'follow'].join(',');
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : null;
  const ogImageUrl = ogImage || DEFAULT_IMAGE;

  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsContent} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="es_ES" />
      {ogType === 'article' && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {ogType === 'article' && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD structured data */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
